import { Button, Chip, Grid, Icon, IconButton, Menu, MenuItem, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import SearchIcon from '@mui/icons-material/Search';
import { useEffect, useRef, useState } from 'react';
import { AppCard } from '../ui';
import { TTipoContaFinanceira } from '../../services/api/cadastros/ContaBancaria';
import { AutoCompleteContaConciliacao } from '../../forms/AutoCompleteContaConciliacao';

interface IFerramentasDaListagemProps {
    mostrarBotaoFecharConta?: boolean;
    mostrarBotaoAbrirConta?: boolean;
    aoClicarEmPesquisa?: (
        datainicial: Dayjs | null,
        datafinal: Dayjs | null,
    ) => void;
    aoClicarEmImpressao?: () => void;
    aoClicarEmFecharConta?: () => void;
    aoClicarEmAbrirConta?: () => void;
    // Só faz sentido pra contas 'recebiveis' — liquidar com a operadora da
    // maquininha (ver DialogTransferirRecebivel).
    mostrarBotaoTransferirRecebivel?: boolean;
    aoClicarEmTransferirRecebivel?: () => void;
    // Faz sentido pra contas 'banco' e 'cofre' — transferência pra uma
    // conta banco ou pra um caixa físico, nunca pra recebíveis (ver
    // DialogTransferenciaConta). O rótulo dos itens do menu muda conforme a
    // origem (ex.: "Saque Bancário" só faz sentido saindo do banco).
    mostrarBotaoTransferencia?: boolean;
    tipoContaOrigemTransferencia?: 'banco' | 'cofre';
    aoClicarEmTransferenciaParaConta?: () => void;
    aoClicarEmTransferenciaParaCaixa?: () => void;
    // Conta 'caixa': transferir do caixa para o Cofre no dia pesquisado (ver
    // DialogTransferenciaCaixaCofre) — inclusive de um dia anterior.
    mostrarBotaoTransferenciaCofre?: boolean;
    aoClicarEmTransferenciaCofre?: () => void;
    // Autocomplete de seleção de conta, exibido antes das datas — agrupa
    // Financeiras (banco/caixa/cofre) e Recebíveis, já dispara a busca dos
    // lançamentos assim que uma conta é escolhida.
    contaSelecionadaId?: string;
    contaSelecionadaLabel?: string;
    contaSelecionadaTipo?: TTipoContaFinanceira;
    aoSelecionarConta?: (idconta: string, descricao: string, tipo?: TTipoContaFinanceira) => void;
    // Só preenchido quando a conta selecionada é tipo='caixa' — sem os
    // botões de Abrir/Fechar Sessão (removidos daqui: só quem está com o
    // caixa aberto pode mexer nele), esse chip é a única pista de que uma
    // transferência pra esse caixa vai ser recusada por falta de sessão.
    statusSessaoCaixa?: 'aberta' | 'fechada';
    // Abre o dialog de detalhamento de saldo por conta (recebíveis, contas
    // bancárias, caixas e cofre individualmente) — não depende de conta
    // selecionada, sempre visível.
    aoClicarEmDetalharSaldos?: () => void;
}

// Conta selecionada, datas, Filtrar e os botões de ação ficam todos numa
// linha só a partir de md — o Chip da conta é compacto de propósito (só o
// nome, sem legenda "Conta selecionada" acima) pra sobrar espaço horizontal
// pros botões não quebrarem linha.
export const FerramentasDaListagemFinanceiroConciliacao: React.FC<
    IFerramentasDaListagemProps
> = ({
    aoClicarEmPesquisa,
    aoClicarEmImpressao,
    aoClicarEmFecharConta,
    aoClicarEmAbrirConta,
    aoClicarEmTransferirRecebivel,
    aoClicarEmTransferenciaParaConta,
    aoClicarEmTransferenciaParaCaixa,
    aoClicarEmTransferenciaCofre,
    aoClicarEmDetalharSaldos,
    mostrarBotaoFecharConta = false,
    mostrarBotaoAbrirConta = false,
    mostrarBotaoTransferirRecebivel = false,
    mostrarBotaoTransferencia = false,
    mostrarBotaoTransferenciaCofre = false,
    tipoContaOrigemTransferencia = 'banco',
    contaSelecionadaId = '',
    contaSelecionadaLabel = '',
    contaSelecionadaTipo,
    aoSelecionarConta,
    statusSessaoCaixa,
}) => {
    const [dataInicial, setDataInicial] = useState<Dayjs | null>(dayjs());
    const [dataFinal, setDataFinal] = useState<Dayjs | null>(dayjs());
    const [anchorTransferencia, setAnchorTransferencia] = useState<HTMLElement | null>(null);

    // 'dia' (padrão): uma data só — lista o caixa daquele dia. 'periodo':
    // data inicial + final, pra puxar vários dias de uma vez.
    const [modoData, setModoData] = useState<'dia' | 'periodo'>('dia');

    const handlePesquisa = () => {
        if (modoData === 'dia') {
            aoClicarEmPesquisa?.(dataInicial, dataInicial);
            return;
        }
        aoClicarEmPesquisa?.(dataInicial, dataFinal);
    };

    // Escolher/digitar uma data já pesquisa, nos dois modos — sem precisar
    // clicar na lupa. Só com conta selecionada (sem ela, a tela avisaria
    // "Selecione uma conta" a cada troca de data), só com data completa
    // (ano com 4 dígitos — enquanto digita, "0002" já é uma data "válida") e
    // com uma espera curta depois da última alteração, pra não disparar uma
    // busca a cada número digitado.
    const dataCompleta = (d: Dayjs | null) => !!d && d.isValid() && d.year() >= 2000 && d.year() <= 2100;
    const timeoutPesquisaRef = useRef<ReturnType<typeof setTimeout>>();
    const pesquisaAutomaticaRef = useRef(false);
    useEffect(() => {
        if (!pesquisaAutomaticaRef.current) return;
        pesquisaAutomaticaRef.current = false;
        if (!contaSelecionadaId) return;
        const inicio = dataInicial;
        const fim = modoData === 'dia' ? dataInicial : dataFinal;
        if (!dataCompleta(inicio) || !dataCompleta(fim) || fim!.isBefore(inicio!, 'day')) return;
        if (timeoutPesquisaRef.current) clearTimeout(timeoutPesquisaRef.current);
        timeoutPesquisaRef.current = setTimeout(() => aoClicarEmPesquisa?.(inicio, fim), 400);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataInicial, dataFinal, modoData]);
    useEffect(() => () => { if (timeoutPesquisaRef.current) clearTimeout(timeoutPesquisaRef.current); }, []);

    const handleMudarDia = (novo: Dayjs | null) => {
        pesquisaAutomaticaRef.current = true;
        setDataInicial(novo);
        setDataFinal(novo);
    };
    const handleMudarInicial = (novo: Dayjs | null) => {
        pesquisaAutomaticaRef.current = true;
        setDataInicial(novo);
    };
    const handleMudarFinal = (novo: Dayjs | null) => {
        pesquisaAutomaticaRef.current = true;
        setDataFinal(novo);
    };

    const labelTransferenciaParaConta = tipoContaOrigemTransferencia === 'cofre'
        ? 'Transferência para Conta Bancária'
        : 'Transferência entre Contas';
    const labelTransferenciaParaCaixa = tipoContaOrigemTransferencia === 'cofre'
        ? 'Transferência para Caixa'
        : 'Saque Bancário';

    return (
        <AppCard sx={{ mx: { xs: 1, sm: 2 }, p: { xs: 1, sm: 1.25 } }}>
            <Grid container spacing={1} alignItems="center">
                <Grid item xs={12} sm="auto" sx={{ minWidth: 0, width: { sm: 260 } }}>
                    <AutoCompleteContaConciliacao
                        contaSelecionadaId={contaSelecionadaId}
                        contaSelecionadaLabel={contaSelecionadaLabel}
                        contaSelecionadaTipo={contaSelecionadaTipo}
                        aoSelecionarConta={(idconta, descricao, tipo) => aoSelecionarConta?.(idconta, descricao, tipo)}
                    />
                </Grid>
                {statusSessaoCaixa && (
                    <Grid item xs="auto">
                        <Tooltip title={statusSessaoCaixa === 'fechada' ? 'Nenhuma movimentação pode ser feita nesse caixa até a sessão ser aberta' : 'Esse caixa está com sessão aberta'}>
                            <Chip
                                label={statusSessaoCaixa === 'aberta' ? 'Sessão aberta' : 'Sessão fechada'}
                                color={statusSessaoCaixa === 'aberta' ? 'success' : 'default'}
                                size="small"
                            />
                        </Tooltip>
                    </Grid>
                )}
                <Grid item xs={12} sm="auto">
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={modoData}
                        onChange={(_, valor: 'dia' | 'periodo' | null) => {
                            if (!valor) return;
                            setModoData(valor);
                            // Voltando pra Dia, o período vira o dia da data inicial.
                            if (valor === 'dia') setDataFinal(dataInicial);
                        }}
                        sx={{ '& .MuiToggleButton-root': { px: 1.5, py: 0.5, textTransform: 'none', fontWeight: 600 }, width: { xs: '100%', sm: 'auto' }, '& > *': { flex: { xs: 1, sm: 'none' } } }}
                    >
                        <ToggleButton value="dia">Dia</ToggleButton>
                        <ToggleButton value="periodo">Período</ToggleButton>
                    </ToggleButtonGroup>
                </Grid>
                {modoData === 'dia' ? (
                    <Grid item xs sm="auto" sx={{ width: { sm: 190 } }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                label="Data do caixa"
                                format="DD/MM/YYYY"
                                value={dataInicial}
                                onChange={handleMudarDia}
                                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                            />
                        </LocalizationProvider>
                    </Grid>
                ) : (
                    <>
                        <Grid item xs={6} sm="auto" sx={{ width: { sm: 178 } }}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="Data inicial"
                                    format="DD/MM/YYYY"
                                    value={dataInicial}
                                    onChange={handleMudarInicial}
                                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={6} sm="auto" sx={{ width: { sm: 178 } }}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="Data final"
                                    format="DD/MM/YYYY"
                                    value={dataFinal}
                                    onChange={handleMudarFinal}
                                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                />
                            </LocalizationProvider>
                        </Grid>
                    </>
                )}
                <Grid item xs="auto">
                    <IconButton color="primary" onClick={handlePesquisa} title="Pesquisar">
                        <SearchIcon sx={{ fontSize: 26 }} />
                    </IconButton>
                </Grid>
                <Grid item xs={6} sm="auto">
                    <Button
                        fullWidth
                        size="small"
                        color="primary"
                        variant="outlined"
                        disableElevation
                        onClick={aoClicarEmImpressao}
                        startIcon={<Icon>print_outlined</Icon>}
                    >
                        Imprimir
                    </Button>
                </Grid>
                <Grid item xs={6} sm="auto">
                    <Button
                        fullWidth
                        size="small"
                        color="primary"
                        variant="outlined"
                        disableElevation
                        onClick={aoClicarEmDetalharSaldos}
                        startIcon={<Icon>account_balance</Icon>}
                    >
                        Detalhar Saldos
                    </Button>
                </Grid>
                {mostrarBotaoTransferirRecebivel && (
                    <Grid item xs={6} sm="auto">
                        <Button
                            fullWidth
                            size="small"
                            color="success"
                            variant="contained"
                            disableElevation
                            onClick={aoClicarEmTransferirRecebivel}
                            startIcon={<Icon>sync_alt</Icon>}
                        >
                            Transferir
                        </Button>
                    </Grid>
                )}
                {mostrarBotaoTransferencia && (
                    <Grid item xs={6} sm="auto">
                        <Button
                            fullWidth
                            size="small"
                            color="success"
                            variant="contained"
                            disableElevation
                            onClick={(e) => setAnchorTransferencia(e.currentTarget)}
                            startIcon={<Icon>sync_alt</Icon>}
                        >
                            Transferência
                        </Button>
                        <Menu
                            anchorEl={anchorTransferencia}
                            open={!!anchorTransferencia}
                            onClose={() => setAnchorTransferencia(null)}
                        >
                            <MenuItem onClick={() => { setAnchorTransferencia(null); aoClicarEmTransferenciaParaConta?.(); }}>
                                {labelTransferenciaParaConta}
                            </MenuItem>
                            <MenuItem onClick={() => { setAnchorTransferencia(null); aoClicarEmTransferenciaParaCaixa?.(); }}>
                                {labelTransferenciaParaCaixa}
                            </MenuItem>
                        </Menu>
                    </Grid>
                )}
                {mostrarBotaoTransferenciaCofre && (
                    <Grid item xs={6} sm="auto">
                        <Button
                            fullWidth
                            size="small"
                            color="success"
                            variant="contained"
                            disableElevation
                            onClick={aoClicarEmTransferenciaCofre}
                            startIcon={<Icon>lock</Icon>}
                        >
                            Transferir p/ Cofre
                        </Button>
                    </Grid>
                )}
                {mostrarBotaoFecharConta && (
                    <Grid item xs={6} sm="auto">
                        <Button
                            fullWidth
                            size="small"
                            color="primary"
                            variant="contained"
                            disableElevation
                            onClick={aoClicarEmFecharConta}
                            startIcon={<Icon>lock</Icon>}
                        >
                            Fechar Conta
                        </Button>
                    </Grid>
                )}
                {mostrarBotaoAbrirConta && (
                    <Grid item xs={6} sm="auto">
                        <Button
                            fullWidth
                            size="small"
                            color="primary"
                            variant="contained"
                            disableElevation
                            onClick={aoClicarEmAbrirConta}
                            startIcon={<Icon>lock_open</Icon>}
                        >
                            Abrir Conta
                        </Button>
                    </Grid>
                )}
            </Grid>
        </AppCard>
    );
};
